// v0.4.45 — Resolve modal with mutually-exclusive two-path picker.
// Path A: corrective_calc_id (validated frozen server-side).
// Path B: no_corrective_reason (≥50 chars after trim).
// UI enforces the same gate as the backend — submit disabled until
// exactly one path is valid. Traffic-light char counter on the reason.
//
// v0.6.12 A5-27 — pick, don't write, on both paths. The corrected filing
// is chosen from the finalised calculations (FiledCalcSelect), not typed
// as an id; the "no correction needed" reason is chosen from
// AMENDMENT_NO_CORRECTION_REASONS, with the counter and textarea shown
// only for "Other".
import React, { useMemo, useState } from "react";
import { resolveAmendment } from "../lib/api";
import { FiledCalcSelect, correctiveCalcFilter } from "./FiledCalcSelect";
import { PresetReasonSelect } from "./PresetReasonSelect";
import { AMENDMENT_NO_CORRECTION_REASONS } from "../lib/reviewReasonPresets";
import { useTranslation } from "react-i18next";

const REASON_MIN = 50;

const counterColour = (n) => {
  if (n >= REASON_MIN) return "text-emerald-700 dark:text-emerald-300";
  if (n >= 30) return "text-amber-600 dark:text-amber-400";
  return "text-rose-500 dark:text-rose-400";
};

export const AmendmentResolveModal = ({ amendment, onClose, onResolved }) => {
  const { t } = useTranslation();
  const [path, setPath] = useState("calc");
  const [calcId, setCalcId] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const trimmedReason = reason.trim();
  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (path === "calc") return calcId.trim().length > 0;
    return trimmedReason.length >= REASON_MIN;
  }, [path, calcId, trimmedReason, submitting]);

  const submit = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const body = path === "calc"
        ? { corrective_calc_id: calcId.trim() }
        : { no_corrective_reason: trimmedReason };
      const res = await resolveAmendment(amendment.id, body);
      onResolved?.(res);
      onClose?.();
    } catch (e) {
      setServerError(
        e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || e?.message
          || "Resolve failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="amendment-resolve-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-2xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700/90">
              {t("ui.amendments.resolve_title", { defaultValue: "Resolve amendment" })}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300 truncate">
              {amendment.id}
            </div>
          </div>
          <button
            data-testid="amendment-resolve-close"
            onClick={onClose}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 px-2"
            aria-label={t("ui.common.close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </div>

        <div className="text-base text-slate-500 dark:text-slate-400 mb-5">
          {amendment.country}
          {amendment.category ? <> · <span className="tabular-nums">{amendment.category}</span></> : null}
        </div>

        <div className="flex gap-2 mb-4 p-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-full text-xs">
          <button
            data-testid="resolve-path-calc"
            onClick={() => setPath("calc")}
            className={`flex-1 py-2 rounded-full transition-colors ${
              path === "calc"
                ? "bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100"
            }`}
          >
            {t("ui.amendments.path_link", { defaultValue: "● Link the corrected filing" })}
          </button>
          <button
            data-testid="resolve-path-noreason"
            onClick={() => setPath("reason")}
            className={`flex-1 py-2 rounded-full transition-colors ${
              path === "reason"
                ? "bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100"
            }`}
          >
            {t("ui.amendments.path_none", { defaultValue: "○ No corrective needed" })}
          </button>
        </div>

        {path === "calc" ? (
          <div className="mb-4">
            <label className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300 block mb-1">
              {t("ui.amendments.corrected_filing", { defaultValue: "Corrected filing" })}
            </label>
            <FiledCalcSelect
              testid="resolve-corrective-calc-id"
              value={calcId}
              onChange={(id) => setCalcId(id)}
              filter={correctiveCalcFilter(amendment)}
              className="text-sm py-2"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              {t("ui.amendments.only_finalised", { defaultValue: "Only finalised calculations for this country and client that have not been replaced by a later version are listed." })}
            </p>
          </div>
        ) : (
          <div className="mb-4">
            <label className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300 block mb-1">
              {t("ui.amendments.no_corrective_reason", { defaultValue: "Reason no corrective is needed" })}
            </label>
            <PresetReasonSelect
              testid="resolve-no-corrective-reason-preset"
              presets={AMENDMENT_NO_CORRECTION_REASONS}
              preset={preset}
              onPick={(p, v) => { setPreset(p); setReason(v); }}
              className="text-sm py-2"
            />
          </div>
        )}

        {serverError && (
          <div
            data-testid="resolve-server-error"
            className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg px-3 py-2 mb-3"
          >
            {serverError}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="resolve-cancel"
            onClick={onClose}
            className="border border-slate-300 dark:border-slate-700 hover:border-slate-500 dark:hover:border-slate-400 text-slate-500 dark:text-slate-400 px-4 py-2 rounded-full text-sm"
          >
            {t("ui.amendments.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            data-testid="resolve-submit"
            data-resolution-class="self-resolve"
            data-resolution-endpoint="POST /api/admin/amendments/{amendment_id}/resolve"
            onClick={submit}
            disabled={!canSubmit}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              canSubmit
                ? "bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed"
            }`}
          >
            {submitting ? t("ui.amendments.resolving", { defaultValue: "Resolving…" }) : t("ui.amendments.resolve", { defaultValue: "Resolve" })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmendmentResolveModal;
